import React from 'react';
import { useSelector } from 'react-redux';
import * as humps from 'humps';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import prodPathReplace from 'shared/prod-path-rewrite';
import { LEARNER_FINAL_SUMMARY_UPDATED_EVENT } from 'timelines/components/completion_criteria/portable-completion-panel/personalized-takeaways-access';
import {
  getLearnerFinalSummaryDetails,
  setLearnerFinalSummaryUpdated,
} from 'redux/actions/help-options';
import { RootState } from 'redux/schemas';
import {
  LearnerFinalSummaryHighlightSection,
  LearnerFinalSummaryStatus,
  LearnerFinalSummaryUpdatedEventData,
} from 'redux/schemas/models/help-options';
import { getCourseAliases } from 'redux/selectors/course';
import { getLearnerFinalSummary } from 'redux/selectors/help-options';
import { getCurrentUser } from 'redux/selectors/users';
import { useAppDispatch } from 'redux/store';
import pusherService from 'shared/services/pusher-service';
import useScrollTop from 'shared/hooks/use-scroll';
import {
  screenMdMax,
} from 'styles/global_defaults/media-queries';
import {
  PERSONALIZED_TAKEAWAYS_SCROLL_EVENT,
  PersonalizedTakeawaysSectionId,
} from './personalized-takeaways-sections';
import fullPageStyles from './personalized-takeaways-page.styles';

const cardLabels = [
  'ASSIGNMENT_PRACTICE',
  'SOCIAL_ACTIVITIES',
  'FEEDBACK_ACTIVITIES',
  'QUIZZES_AND_SURVEYS',
  'EVENTS',
] as const;

const emptyContentTranslationKeys = [
  'AT_A_GLANCE',
  'IN_THE_NUMBERS',
  'COURSE_HIGHLIGHTS',
  'YOUR_STRENGTHS',
  'YOUR_NEXT_LEVEL',
  'KEEP_LEARNING',
  'YOU_DID_IT',
  'DISCLAIMER',
] as const;

type EmptyContentTranslationKey = typeof emptyContentTranslationKeys[number];

type InTheNumbersPayload = {
  submissions?: number;
  quizzes?: number;
  discussions?: number;
  rolePlays?: number;
  role_plays?: number;
  eventsAttended?: number;
  events_attended?: number;
};

type SectionContentPayload = {
  headline?: string;
  summary?: string;
  intro?: string;
  bullets?: string[];
  resources?: Array<{
    title?: string | null;
    reason?: string;
    lecturePageId?: number;
    lecture_page_id?: number;
    componentId?: number;
    component_id?: number;
  }>;
};

type SectionResource = {
  title: string;
  reason: string;
  lecturePageId?: number;
  componentId?: number;
};

type DisclaimerContentPayload = {
  submissionCount?: number | string;
  submission_count?: number | string;
  rolePlayCount?: number | string;
  role_play_count?: number | string;
};

type SectionText = {
  headline: string;
  summary: string;
  intro: string;
  bullets: string[];
  resources: SectionResource[];
  emptyMessage: string;
};

const getLecturePageHref = (
  catalogId?: string,
  lecturePageId?: number,
  componentId?: number,
) => {
  if (!catalogId || !lecturePageId) {
    return '';
  }

  const base = `#!/courses/${catalogId}/lecture_pages/${lecturePageId}`;

  if (!componentId) {
    return base;
  }

  return `${base}?lectureActivityId=${componentId}`;
};

const isEmptyContentTranslationKey = (key: string): key is EmptyContentTranslationKey => (
  emptyContentTranslationKeys.includes(key as EmptyContentTranslationKey)
);

const getEmptyContentTranslation = (emptyContentI18nKey?: string, currentUserFirstName?: string) => {
  const [sectionKey, contentKey] = emptyContentI18nKey?.split('.') || [];

  if (contentKey !== 'empty_content' || !sectionKey) {
    return '';
  }

  const translationKey = sectionKey.toUpperCase();

  if (!isEmptyContentTranslationKey(translationKey)) {
    return '';
  }

  return t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.EMPTY_CONTENT[translationKey](translationKey === 'AT_A_GLANCE' ? currentUserFirstName : undefined);
};

const getSectionText = (
  section?: LearnerFinalSummaryHighlightSection,
  currentUserFirstName?: string,
) => {
  if (!section) {
    return {
      headline: '',
      summary: '',
      intro: '',
      bullets: [],
      resources: [],
      emptyMessage: '',
    } as SectionText;
  }

  const contentPayload = section.sectionPayload?.content as SectionContentPayload | undefined;

  const headline = typeof contentPayload?.headline === 'string'
    ? contentPayload.headline.trim()
    : '';
  const summary = typeof contentPayload?.summary === 'string'
    ? contentPayload.summary.trim()
    : '';
  const intro = typeof contentPayload?.intro === 'string'
    ? contentPayload.intro.trim()
    : '';
  const bullets = Array.isArray(contentPayload?.bullets)
    ? contentPayload.bullets.filter((bullet): bullet is string => typeof bullet === 'string' && bullet.trim() !== '')
      .map((bullet) => bullet.trim())
    : [];
  const resources = Array.isArray(contentPayload?.resources)
    ? contentPayload.resources
      .map((resource, index) => {
        const title = typeof resource?.title === 'string' && resource.title.trim() !== ''
          ? resource.title.trim()
          : t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.RESOURCE(`${index + 1}`);
        const reason = typeof resource?.reason === 'string' ? resource.reason.trim() : '';
        const lecturePageId = typeof resource?.lecturePageId === 'number'
          ? resource.lecturePageId
          : resource?.lecture_page_id;
        const componentId = typeof resource?.componentId === 'number'
          ? resource.componentId
          : resource?.component_id;

        return {
          title,
          reason,
          lecturePageId,
          componentId,
        };
      })
      .filter((resource) => resource.reason !== '')
    : [];
  const emptyContentI18nKey = section.sectionPayload?.emptyContentI18nKey;
  const emptyMessage = getEmptyContentTranslation(emptyContentI18nKey, currentUserFirstName);

  return {
    headline,
    summary,
    intro,
    bullets,
    resources,
    emptyMessage,
  } as SectionText;
};

const getInTheNumbersPayload = (section?: LearnerFinalSummaryHighlightSection): InTheNumbersPayload => (
  section?.sectionPayload as InTheNumbersPayload || {}
);

const getInTheNumbersCards = (section?: LearnerFinalSummaryHighlightSection) => {
  const payload = getInTheNumbersPayload(section);

  return [
    {
      key: 'ASSIGNMENT_PRACTICE',
      value: payload.submissions,
    },
    {
      key: 'SOCIAL_ACTIVITIES',
      value: payload.discussions,
    },
    {
      key: 'FEEDBACK_ACTIVITIES',
      value: payload.rolePlays ?? payload.role_plays,
    },
    {
      key: 'QUIZZES_AND_SURVEYS',
      value: payload.quizzes,
    },
    {
      key: 'EVENTS',
      value: payload.eventsAttended ?? payload.events_attended,
    },
  ] as const;
};

const getListIconBySectionType = (
  sectionType: string,
  listType: 'bullets' | 'resources',
) => {
  if (listType === 'resources' && sectionType === 'keep_learning') {
    return 'expand';
  }

  if (listType === 'bullets' && sectionType === 'your_next_level') {
    return 'ideas';
  }

  if (listType === 'bullets' && sectionType === 'your_strengths') {
    return 'goal';
  }

  return listType === 'resources' ? 'expand' : 'goal';
};

const renderSectionContent = (
  section?: LearnerFinalSummaryHighlightSection,
  catalogId?: string,
  currentUserFirstName?: string,
) => {
  const {
    headline,
    summary,
    intro,
    bullets,
    resources,
    emptyMessage,
  } = getSectionText(section, currentUserFirstName);
  const sectionType = section?.sectionType || '';
  const bulletIcon = getListIconBySectionType(sectionType, 'bullets');
  const resourceIcon = getListIconBySectionType(sectionType, 'resources');

  if (headline || summary || intro || bullets.length > 0 || resources.length > 0) {
    return (
      <div className='section-copy'>
        {headline && <p className='section-headline text-large-regular'>{headline}</p>}
        {summary && <p className='section-summary text-regular mb-0'>{summary}</p>}
        {intro && <p className='section-intro text-regular mb-0'>{intro}</p>}
        {bullets.length > 0 && (
          <ul className='section-bullets text-regular'>
            {bullets.map((bullet, index) => (
              <li key={`${index}-${bullet}`} className='section-bullet-item semi-bold'>
                <NvIcon className='section-bullet-icon' icon={bulletIcon} size='smallest' />
                <span className='section-bullet-text'>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
        {resources.length > 0 && (
          <div className='section-resources'>
            {resources.map((resource) => (
              <div key={`${resource.title}-${resource.reason}`} className='resource-card'>
                <NvIcon className='resource-icon' icon={resourceIcon} size='smallest' />
                <div>
                  <span className='resource-reason text-regular'>{resource.reason}</span>
                  {' '}
                  {getLecturePageHref(catalogId, resource.lecturePageId, resource.componentId)
                    ? (
                      <a
                        className='resource-title text-medium semi-bold'
                        href={getLecturePageHref(catalogId, resource.lecturePageId, resource.componentId)}
                      >
                        {resource.title}
                      </a>
                    )
                    : <span className='resource-title text-medium semi-bold'>{resource.title}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (emptyMessage) {
    return <p className='section-empty text-large-body mb-0'>{emptyMessage}</p>;
  }

  return null;
};

type LoadingSectionProps = {
  id: PersonalizedTakeawaysSectionId;
  kicker: string;
  title: string;
};

const LoadingSection = ({ id, kicker, title }: LoadingSectionProps) => (
  <section id={id} className='section'>
    <div className='divider' />
    <h2 className='section-kicker course-title-xs mb-0'>{kicker}</h2>
    <h3 className='section-title course-title-l mt-0'>{title}</h3>
    <div className='generating-block'>
      <GradientAiIcon icon='gen-ai' size='medium' />
      <div className='generating-label text-small semi-bold'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.GENERATING()}</div>
    </div>
  </section>
);

type SummarySectionProps = {
  id: PersonalizedTakeawaysSectionId;
  kicker: string;
  title: string;
  catalogId?: string;
  currentUserFirstName?: string;
  summarySection?: LearnerFinalSummaryHighlightSection;
};

const SummarySection = ({
  id,
  kicker,
  title,
  catalogId,
  currentUserFirstName,
  summarySection,
}: SummarySectionProps) => {
  if (!summarySection) {
    return <LoadingSection id={id} kicker={kicker} title={title} />;
  }

  return (
    <section id={id} className='section'>
      <div className='divider' />
      <h2 className='section-kicker course-title-xs mb-0'>{kicker}</h2>
      <h3 className='section-title course-title-l mt-0'>{title}</h3>
      {renderSectionContent(summarySection, catalogId, currentUserFirstName)}
    </section>
  );
};

const PersonalizedTakeawaysPage = () => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state, $rootScope] = injectServices(['$state', '$rootScope']);
  const dispatch = useAppDispatch();
  const requestedSummaryIdRef = React.useRef<number | null>(null);
  const [hasAttemptedInitialHighlightsLoad, setHasAttemptedInitialHighlightsLoad] = React.useState(false);
  const mainGridCtrl = $rootScope?.$$childHead?.MainGridCtrl;
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);
  const courseId = useSelector((state: RootState) => state.app.currentCourseId);
  const aliases = useSelector((state: RootState) => getCourseAliases(state));
  const courseAliases = aliases?.courseAliases || {};
  const currentUser = useSelector(getCurrentUser);
  const fromState = $state.params?.from;
  const learnerFinalSummaryReportId = $state.params?.learnerFinalSummaryReportId;
  const currentUserFirstName = currentUser?.firstName;
  const hasLearnerFinalSummaryReportId = !!learnerFinalSummaryReportId;
  const learnerFinalSummary = useSelector((state: RootState) => getLearnerFinalSummary(state, currentUser?.id));
  const learnerFinalSummaryStatus = learnerFinalSummary?.status;
  const highlightSections = React.useMemo<LearnerFinalSummaryHighlightSection[]>(
    () => (hasLearnerFinalSummaryReportId ? (learnerFinalSummary?.highlightSections || []) : []),
    [hasLearnerFinalSummaryReportId, learnerFinalSummary?.highlightSections],
  );
  const hasLoadedHighlights = highlightSections.length > 0;
  const sectionsByType = React.useMemo<Record<string, LearnerFinalSummaryHighlightSection>>(() => {
    const nextSections: Record<string, LearnerFinalSummaryHighlightSection> = {};

    highlightSections.forEach((section: LearnerFinalSummaryHighlightSection) => {
      nextSections[humps.camelize(section.sectionType)] = section;
    });

    return nextSections;
  }, [highlightSections]);
  const isScrolled = useScrollTop(document.getElementById('main-panel') || document.body);

  React.useEffect(() => {
    requestedSummaryIdRef.current = null;
    setHasAttemptedInitialHighlightsLoad(false);
  }, [learnerFinalSummaryReportId]);

  React.useEffect(() => {
    if (!catalogId || !currentUser?.id || !learnerFinalSummaryReportId || hasLoadedHighlights) {
      if (hasLoadedHighlights && !hasAttemptedInitialHighlightsLoad) {
        setHasAttemptedInitialHighlightsLoad(true);
      }
      return () => {};
    }

    if (requestedSummaryIdRef.current === learnerFinalSummaryReportId) {
      return () => {};
    }

    requestedSummaryIdRef.current = learnerFinalSummaryReportId;

    const requestPromise = dispatch(getLearnerFinalSummaryDetails({
      catalogId,
      userId: currentUser.id,
      learnerFinalSummaryReportId,
    }));

    requestPromise.finally(() => {
      setHasAttemptedInitialHighlightsLoad(true);
    });

    return () => {
      requestPromise.abort();
    };
  }, [
    catalogId,
    currentUser?.id,
    dispatch,
    hasAttemptedInitialHighlightsLoad,
    hasLoadedHighlights,
    learnerFinalSummaryReportId,
  ]);

  React.useEffect(() => {
    if (
      !courseId
      || !catalogId
      || !currentUser?.id
      || !learnerFinalSummaryReportId
      || !hasAttemptedInitialHighlightsLoad
      || hasLoadedHighlights
    ) {
      return () => {};
    }

    const pusherChannel = pusherService.initializeCourseChannel(courseId);
    if (!pusherChannel) {
      return () => {};
    }

    const handleLearnerFinalSummaryUpdated = (pusherData: unknown) => {
      const data = humps.camelizeKeys(pusherData as Record<string, unknown>) as LearnerFinalSummaryUpdatedEventData;

      if (data.userId !== currentUser.id || data.learnerFinalSummaryReportId !== learnerFinalSummaryReportId) {
        return;
      }

      dispatch(setLearnerFinalSummaryUpdated({
        learnerFinalSummaryReportId: data.learnerFinalSummaryReportId,
        userId: currentUser.id,
        status: data.status,
        available: data.available,
        generatedAt: data.generatedAt,
        errorMessage: data.errorMessage,
      }));

      if (data.status === LearnerFinalSummaryStatus.COMPLETED && data.available) {
        dispatch(getLearnerFinalSummaryDetails({
          catalogId,
          userId: currentUser.id,
          learnerFinalSummaryReportId: data.learnerFinalSummaryReportId,
        }));
      }
    };

    pusherChannel.bind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleLearnerFinalSummaryUpdated);

    return () => {
      pusherChannel.unbind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleLearnerFinalSummaryUpdated);
    };
  }, [
    catalogId,
    courseId,
    currentUser?.id,
    dispatch,
    hasAttemptedInitialHighlightsLoad,
    hasLoadedHighlights,
    learnerFinalSummaryReportId,
    learnerFinalSummaryStatus,
  ]);

  const atAGlanceSection = sectionsByType.atAGlance;
  const inTheNumbersSection = sectionsByType.inTheNumbers;
  const courseHighlightsSection = sectionsByType.courseHighlights;
  const yourStrengthsSection = sectionsByType.yourStrengths;
  const yourNextLevelSection = sectionsByType.yourNextLevel;
  const keepLearningSection = sectionsByType.keepLearning;
  const disclaimerSection = sectionsByType.disclaimer;
  const disclaimerContent = disclaimerSection?.sectionPayload?.content as DisclaimerContentPayload | undefined;
  const submissionInsights = String(disclaimerContent?.submissionCount ?? disclaimerContent?.submission_count ?? 0);
  const rolePlayInsights = String(disclaimerContent?.rolePlayCount ?? disclaimerContent?.role_play_count ?? 0);
  const inTheNumbersContent = renderSectionContent(inTheNumbersSection, catalogId, currentUserFirstName);
  const inTheNumbersCards = getInTheNumbersCards(inTheNumbersSection);
  const visibleInTheNumbersCards = inTheNumbersCards.filter(
    (card) => typeof card.value === 'number' && card.value > 0,
  );

  React.useEffect(() => {
    const onScrollSection = (event: CustomEvent<{ sectionId?: PersonalizedTakeawaysSectionId }>) => {
      const targetId = event?.detail?.sectionId;

      if (!targetId) {
        return;
      }

      document.getElementById(targetId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };

    const listener = onScrollSection as unknown as EventListener;
    window.addEventListener(PERSONALIZED_TAKEAWAYS_SCROLL_EVENT, listener);

    return () => {
      window.removeEventListener(PERSONALIZED_TAKEAWAYS_SCROLL_EVENT, listener);
    };
  }, []);

  React.useEffect(() => {
    const syncLhsMode = () => {
      if (mainGridCtrl?.shouldHideLHS?.()) {
        return;
      }

      const isLarge = window.innerWidth > screenMdMax;

      // Collapse desktop LHS for medium *and* small screens; desktop nav isn't shown there.
      if (!isLarge) {
        if (mainGridCtrl?.isLhsExpandedDesktop) {
          mainGridCtrl.isLhsExpandedDesktop = false;
          mainGridCtrl.toggleLhsDesktop?.();
        }
        return;
      }

      if (mainGridCtrl && !mainGridCtrl.isLhsExpandedDesktop) {
        mainGridCtrl.isLhsExpandedDesktop = true;
        mainGridCtrl.toggleLhsDesktop?.();
      }
    };

    syncLhsMode();
    window.addEventListener('resize', syncLhsMode);

    return () => {
      window.removeEventListener('resize', syncLhsMode);
    };
  }, [mainGridCtrl]);

  const onBackToCourse = () => {
    if (!catalogId) {
      return;
    }

    if (fromState === 'learner-progress') {
      $state.go('learner-progress', { catalogId }, { replace: true });
      return;
    }

    $state.go('course-home', { catalogId });
  };

  const onOpenMobileMenu = () => {
    mainGridCtrl?.toggleLhsMobile?.();
  };

  return (
    <main css={fullPageStyles}>
      <div className={`mobile-burger-bar ${isScrolled ? 'scrolled' : ''}`}>
        <button
          type='button'
          className='mobile-burger-button'
          aria-label={t.LHS.OPEN_LHS()}
          onClick={onOpenMobileMenu}
        >
          <NvIcon icon='menu' size='small' />
        </button>
      </div>
      <div className='takeaways-content'>
        <div className='takeaways-spark'>
          <GradientAiIcon icon='gen-ai' size='large' />
        </div>
        <h1 className='eyebrow course-title-xs mt-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.HEADER()}</h1>
        <div className='main-title header-xl font-weight-bold'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.TITLE()}</div>

        <section id={PersonalizedTakeawaysSectionId.AT_A_GLANCE} className='section'>
          <div className='divider' />
          <h2 className='section-kicker course-title-xs mb-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.AT_A_GLANCE()}</h2>
          {atAGlanceSection ? renderSectionContent(atAGlanceSection, catalogId, currentUserFirstName) : (
            <div className='generating-block'>
              <GradientAiIcon icon='gen-ai' size='medium' />
              <div className='generating-label text-small semi-bold'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.GENERATING()}</div>
            </div>
          )}
        </section>

        <section id={PersonalizedTakeawaysSectionId.IN_THE_NUMBERS} className='section'>
          <div className='divider' />
          <h2 className='section-kicker course-title-xs mb-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.IN_THE_NUMBERS()}</h2>
          <h3 className='section-title course-title-l mt-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.HOW_YOU_SHOWED_UP()}</h3>
          {inTheNumbersSection ? (
            inTheNumbersContent
            || (visibleInTheNumbersCards.length > 0 ? (
              <div className='stats-row'>
                {visibleInTheNumbersCards.map((card) => (
                  <div key={card.key} className='stats-card'>
                    <div className='stat-value header-lg'>{card.value}</div>
                    <div className='text-medium semi-bold'>
                      {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE[card.key]()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className='section-empty text-large-body mb-0'>
                {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.EMPTY_CONTENT.IN_THE_NUMBERS()}
              </p>
            ))
          ) : (
            <div className='stats-row'>
              {cardLabels.map((labelKey) => (
                <div key={labelKey} className='stats-card'>
                  <div className='mini-spinner' />
                  <div className='text-medium semi-bold'>
                    {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE[labelKey]()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <SummarySection
          id={PersonalizedTakeawaysSectionId.COURSE_HIGHLIGHTS}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.COURSE_HIGHLIGHTS(courseAliases)}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.WHAT_MATTERED_MOST()}
          catalogId={catalogId}
          currentUserFirstName={currentUserFirstName}
          summarySection={courseHighlightsSection}
        />
        <SummarySection
          id={PersonalizedTakeawaysSectionId.YOUR_STRENGTHS}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOUR_STRENGTHS()}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.WHERE_YOU_EXCELLED()}
          catalogId={catalogId}
          currentUserFirstName={currentUserFirstName}
          summarySection={yourStrengthsSection}
        />
        <SummarySection
          id={PersonalizedTakeawaysSectionId.YOUR_NEXT_LEVEL}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOUR_NEXT_LEVEL()}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.WHERE_YOU_CAN_GROW()}
          catalogId={catalogId}
          currentUserFirstName={currentUserFirstName}
          summarySection={yourNextLevelSection}
        />
        <SummarySection
          id={PersonalizedTakeawaysSectionId.KEEP_LEARNING}
          kicker={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.KEEP_LEARNING()}
          title={t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.MOMENTS_WORTH_REVISITING()}
          catalogId={catalogId}
          currentUserFirstName={currentUserFirstName}
          summarySection={keepLearningSection}
        />

        <section id={PersonalizedTakeawaysSectionId.CONGRATULATIONS} className='section completion-copy mb-0'>
          <div className='completion-badge'>
            <img
              className='congrats-image'
              src={prodPathReplace('images/congratulations.png')}
              alt=''
            />
          </div>
          <h2 className='section-kicker course-title-xs mb-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.YOU_DID_IT(currentUser?.firstName ?? '')}</h2>
          <h3 className='section-title course-title-l mt-0'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.CONGRATULATIONS()}</h3>
          <p className='completion-body text-large-body'>{t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.COMPLETION_MESSAGE(courseAliases)}</p>
          <button type='button' className='back-button text-medium semi-bold' onClick={onBackToCourse}>
            {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.BACK_TO_COURSE(courseAliases)}
          </button>
          <p className='note text-small semi-bold mb-0'>
            {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.PAGE.NOTE({
              ...courseAliases,
              rolePlayInsights,
              submissionInsights,
            })}
          </p>
        </section>
      </div>
    </main>
  );
};

export default PersonalizedTakeawaysPage;
